import type { FeedItem } from "@compass/shared/schema/api/feed";
import {
    formatPreviewDuration,
    formatPreviewViewCount,
    getBlogPostPreviewThumbnail,
    isVideoPreview,
} from "@compass/shared/utils";
import { Link } from "@tanstack/react-router";
import { BookOpen, Play } from "lucide-react";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { formatISODateRelative } from "lib/formatISODate";

type Props = Extract<FeedItem, { type: "newBlogPost" }>;

export const FeedBlogPostCard = ({ blogPost }: Props) => {
    const thumbnail = getBlogPostPreviewThumbnail(blogPost.preview);
    const isVideo = isVideoPreview(blogPost.preview);
    const stats = blogPost.preview && "stats" in blogPost.preview ? blogPost.preview.stats : null;

    const hasPreview = Boolean(thumbnail) || isVideo;

    const objectFitClass = blogPost.previewFit === "contain" ? "object-contain" : "object-cover";
    const objectPositionStyle =
        blogPost.previewFit === "manual"
            ? {
                  objectPosition: `${blogPost.previewPositionX ?? 50}% ${blogPost.previewPositionY ?? 50}%`,
              }
            : undefined;

    return (
        <Link
            to="/p/$slug"
            params={{ slug: blogPost.slug }}
            className="block overflow-hidden rounded-2xl border border-purple-200 bg-white no-underline shadow-sm transition-shadow hover:shadow-md"
        >
            <div className="flex gap-4 p-4">
                {hasPreview && (
                    <div className="relative aspect-square w-24 flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 sm:w-32">
                        {thumbnail ? (
                            <img
                                src={thumbnail}
                                alt={blogPost.title}
                                className={`h-full w-full ${objectFitClass}`}
                                style={objectPositionStyle}
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                                Video
                            </div>
                        )}
                        {isVideo && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60">
                                    <Play className="ml-0.5 h-5 w-5 fill-white text-white" />
                                </div>
                            </div>
                        )}
                        {isVideo && stats?.durationSeconds != null && (
                            <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                                {formatPreviewDuration(stats.durationSeconds)}
                            </span>
                        )}
                    </div>
                )}
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                            <BookOpen className="h-4 w-4 text-purple-500" />
                            <span className="text-xs font-medium text-purple-600">Blog Post</span>
                        </div>
                        <FavoriteButton target={{ type: "blogPost", id: blogPost.id }} size={16} />
                    </div>
                    <h3 className="line-clamp-2 text-base font-semibold text-gray-900">
                        {blogPost.title}
                    </h3>
                    {blogPost.excerpt && (
                        <p className="line-clamp-2 mt-1 text-sm text-gray-600">
                            {blogPost.excerpt}
                        </p>
                    )}
                    <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-3 text-xs text-gray-400">
                        {blogPost.author.userId != null ? (
                            <Link
                                to="/social/profile/$id"
                                params={{ id: String(blogPost.author.userId) }}
                                onClick={(e) => e.stopPropagation()}
                                className="text-gray-500 underline"
                            >
                                {blogPost.author.name}
                            </Link>
                        ) : (
                            <span>{blogPost.author.name}</span>
                        )}
                        {blogPost.publishedAt && (
                            <>
                                <span>·</span>
                                <span>{formatISODateRelative(blogPost.publishedAt)}</span>
                            </>
                        )}
                        {isVideo && stats?.viewCount != null && (
                            <>
                                <span>·</span>
                                <span>{formatPreviewViewCount(stats.viewCount)} views</span>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};
